/**
 * Lot Inventory — the FIFO bank, in queue order.
 *
 * Rows appear oldest vintage first, then dairy roster position, because that is
 * exactly the order the allocation consumes them. Read top to bottom, the table
 * *is* the queue: whatever sits at the bottom is what gets starved when a
 * month's claimed capacity falls short of production.
 *
 * Age is the signal worth encoding in form rather than leaving as a number. A
 * lot that has sat for months is either a deliberate hold or something nobody
 * noticed — the workbook this replaces has carried a 0.52 MMBtu remainder since
 * February 2026, re-dispensed one row at a time, quarter after quarter.
 *
 * Backend: there is no `/lcfs/allocation/lots` endpoint. The queue is built in
 * the browser by `lib/allocationView.ts` from the four reference lists omai does
 * serve — `/lcfs/dairies`, `/lcfs/fse`, `/lcfs/production`, `/lcfs/capacities`
 * — replayed from the first period, because a period opens on the previous
 * one's remainder. Porting that merge to Python is the remaining half of the
 * move (locked decision #6: the UI never recomputes a compliance figure); see
 * the note above `useAllocationInputs` in `api/hooks.ts`.
 */

import { Decimal } from "@compliance/domain";
import {
  Alert,
  Badge,
  Group,
  Paper,
  Progress,
  ScrollArea,
  Select,
  Skeleton,
  Stack,
  Table,
  Text,
  Tooltip,
} from "@mantine/core";
import { IconAlertCircle } from "@tabler/icons-react";
import { useSearchParams } from "react-router-dom";
import { useLotInventory } from "../api/hooks";
import type { LotRow } from "../api/types";
import { formatApiError } from "../lib/errors";
import { fmtMmbtu as fmt } from "../lib/mmbtuFormat";

/** Periods a lot may sit before it is worth pointing at. */
const STALE_AFTER = 3;

function dec(value: string): Decimal {
  try {
    return new Decimal(value);
  } catch {
    return new Decimal(0);
  }
}

/**
 * Consumed share of a lot, for the drain bar.
 *
 * Measured against the period's OPENING balance, not the vintage's lifetime
 * production. Dividing by production made a carried lot that finished this
 * period read as 0% drained — Peterson's July vintage came into August with
 * 0.01 MMBtu left, spent all of it, and showed an empty bar against 1,709.09
 * produced back in July.
 */
function drained(row: LotRow): number {
  const opening = dec(row.opening.value);
  if (opening.lte(0)) return 0;
  const n = dec(row.consumed.value).div(opening).times(100).toNumber();
  return Number.isFinite(n) ? Math.max(0, Math.min(100, n)) : 0;
}

/**
 * Where a lot's production figure came from (BIO-381).
 *
 * Silent for a transcribed figure, which is what every figure was before the
 * derivation existed — a badge on all 208 of them would be noise. A derived one
 * says so, and says how many of the month's days leaned on a tier below the
 * custody meter, because that is the disclosure a verifier is owed: the backend
 * measures and records it, and a screen that drops it is the only reason it
 * would go unseen.
 */
function Provenance({ row }: { row: LotRow }) {
  if (row.source !== "telemetry") return null;
  const substituted = row.substituted_days;
  return (
    <Tooltip
      label={
        substituted > 0
          ? `Derived from the meters: the injected energy at the custody-transfer point, split by this dairy's own meter and analyzer. ${substituted} day${substituted === 1 ? "" : "s"} of the month came from a tier below the custody meter — the GC outlet flow, or the plant's consolidated record.`
          : "Derived from the meters: the injected energy at the custody-transfer point, split by this dairy's own meter and analyzer. Every day measured at the custody meter."
      }
      multiline
      w={320}
      withArrow
    >
      <Badge
        color={substituted > 0 ? "yellow" : "teal"}
        variant="light"
        size="xs"
        style={{ cursor: "help" }}
      >
        {substituted > 0 ? `derived · ${substituted}d substituted` : "derived"}
      </Badge>
    </Tooltip>
  );
}

export function LotInventoryPage() {
  const [params, setParams] = useSearchParams();
  const period = params.get("period") ?? undefined;
  const lotsQ = useLotInventory(period);

  const setPeriod = (value: string) => {
    const p = new URLSearchParams(params);
    if (value) p.set("period", value);
    else p.delete("period");
    setParams(p, { replace: true });
  };

  const data = lotsQ.data;
  const banked = data ? dec(data.banked_total.value) : new Decimal(0);

  return (
    <Stack gap="lg">
      {/* No section heading: the tab label is it — see `routes/Lcfs.tsx`. The
          back-link to the ledger went with it; the ledger is the previous tab,
          one row above this, and the period carries across when you switch. */}
      <Group justify="space-between" wrap="wrap" gap="md" align="flex-start">
        <Text c="dimmed" size="sm" maw={800}>
          Production banked by dairy and vintage, in the order the allocation consumes it — oldest
          first, then dairy roster position. Whatever sits at the bottom of this queue is what gets
          starved when a month's claimed capacity falls short of production. Opening, consumed and
          remaining are all scoped to this period, so every row closes; hover an opening balance for
          the vintage's original production.
        </Text>
        {data ? (
          <Select
            aria-label="Dispensing period"
            data={data.available_periods}
            value={data.period}
            onChange={(v) => setPeriod(v ?? "")}
            w={140}
            allowDeselect={false}
            comboboxProps={{ withinPortal: true }}
          />
        ) : null}
      </Group>

      {lotsQ.isPending ? (
        <Skeleton height={420} radius="md" />
      ) : lotsQ.isError ? (
        <Alert color="red" variant="light" icon={<IconAlertCircle size={16} />}>
          <Text size="sm">{formatApiError(lotsQ.error)}</Text>
        </Alert>
      ) : !data ? null : (
        <>
          <Paper p="md" radius="md" withBorder>
            <Group justify="space-between" wrap="wrap" gap="lg">
              <Stat
                label="Banked after this period"
                value={`${fmt(data.banked_total.value)} MMBtu`}
                sub={banked.gt("0.0005") ? "carries to the next period" : "queue fully drained"}
              />
              <Stat label="Lots in view" value={String(data.items.length)} sub="dairy × vintage" />
              <Stat
                label="Oldest banked vintage"
                value={data.oldest_banked_vintage ?? "Nothing banked"}
                sub={
                  data.oldest_banked_vintage === null
                    ? "queue drained completely"
                    : data.oldest_age_periods === 0
                      ? "current vintage only"
                      : data.oldest_age_periods >= STALE_AFTER
                        ? `+${data.oldest_age_periods} periods · past the review threshold`
                        : `+${data.oldest_age_periods} period${data.oldest_age_periods === 1 ? "" : "s"}`
                }
              />
            </Group>
          </Paper>

          {data.items.length === 0 ? (
            <Alert color="gray" variant="light" icon={<IconAlertCircle size={16} />}>
              <Text size="sm">No lots for this period.</Text>
            </Alert>
          ) : (
            <Paper radius="md" withBorder>
              <ScrollArea>
                <Table striped highlightOnHover verticalSpacing="sm" miw={900}>
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th>Dairy</Table.Th>
                      <Table.Th>Vintage</Table.Th>
                      <Table.Th>Age</Table.Th>
                      <Table.Th>Pathway code</Table.Th>
                      <Table.Th ta="right">Opening</Table.Th>
                      <Table.Th ta="right">Consumed</Table.Th>
                      <Table.Th ta="right">Remaining</Table.Th>
                      <Table.Th w={110}>Drained</Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {data.items.map((row) => {
                      const left = dec(row.remaining.value);
                      const stale = row.age_periods >= STALE_AFTER && left.gt("0.005");
                      return (
                        <Table.Tr key={`${row.dairy_id}:${row.vintage_month}`}>
                          <Table.Td>
                            <Text size="sm">{row.dairy_name}</Text>
                          </Table.Td>
                          <Table.Td>
                            <Group gap={6} wrap="nowrap">
                              <Text size="sm" ff="monospace">
                                {row.vintage_month}
                              </Text>
                              <Provenance row={row} />
                            </Group>
                          </Table.Td>
                          <Table.Td>
                            {row.age_periods === 0 ? (
                              <Text size="xs" c="dimmed">
                                current
                              </Text>
                            ) : (
                              <Tooltip
                                label={
                                  stale
                                    ? "Banked longer than a quarter and still carrying volume — a deliberate hold, or something nobody noticed."
                                    : `Banked ${row.age_periods} period${row.age_periods === 1 ? "" : "s"}`
                                }
                                multiline
                                w={280}
                                withArrow
                              >
                                <Badge
                                  color={stale ? "orange" : "grape"}
                                  variant={stale ? "filled" : "light"}
                                  size="sm"
                                >
                                  +{row.age_periods}
                                </Badge>
                              </Tooltip>
                            )}
                          </Table.Td>
                          <Table.Td>
                            <Text size="xs" ff="monospace" c="dimmed">
                              {row.fpc_code}
                            </Text>
                          </Table.Td>
                          <Table.Td ta="right">
                            <Tooltip
                              label={`${fmt(row.produced.value)} MMBtu produced in ${row.vintage_month}${
                                row.age_periods > 0 ? ", partly dispensed in earlier periods" : ""
                              }`}
                              withArrow
                            >
                              <Text
                                size="sm"
                                ff="monospace"
                                style={{ fontVariantNumeric: "tabular-nums" }}
                              >
                                {fmt(row.opening.value)}
                              </Text>
                            </Tooltip>
                          </Table.Td>
                          <Table.Td ta="right">
                            <Text
                              size="sm"
                              ff="monospace"
                              c="dimmed"
                              style={{ fontVariantNumeric: "tabular-nums" }}
                            >
                              {fmt(row.consumed.value)}
                            </Text>
                          </Table.Td>
                          <Table.Td ta="right">
                            <Text
                              size="sm"
                              ff="monospace"
                              fw={left.gt("0.005") ? 700 : 400}
                              c={left.gt("0.005") ? (stale ? "orange" : undefined) : "dimmed"}
                              style={{ fontVariantNumeric: "tabular-nums" }}
                            >
                              {fmt(row.remaining.value)}
                            </Text>
                          </Table.Td>
                          <Table.Td>
                            <Progress
                              value={drained(row)}
                              color={left.gt("0.005") ? (stale ? "orange" : "indigo") : "teal"}
                              size="sm"
                              radius="sm"
                              aria-label={`${Math.round(drained(row))}% drained`}
                            />
                          </Table.Td>
                        </Table.Tr>
                      );
                    })}
                  </Table.Tbody>
                </Table>
              </ScrollArea>
            </Paper>
          )}

          <Text size="xs" c="dimmed">
            Computed {data.computed_at.slice(0, 10)}. A lot's remaining balance is its opening
            balance for the next period.
          </Text>
        </>
      )}
    </Stack>
  );
}

function Stat({ label, value, sub }: { label: string; value: string; sub: string }) {
  return (
    <Stack gap={0}>
      <Text size="xs" c="dimmed" tt="uppercase" fw={600} lts="0.04em">
        {label}
      </Text>
      <Text size="lg" fw={700} style={{ fontVariantNumeric: "tabular-nums" }}>
        {value}
      </Text>
      <Text size="xs" c="dimmed">
        {sub}
      </Text>
    </Stack>
  );
}
